import { withLogParticipants } from '../shared/log-participant-snapshot';
import { saveAppliedTruth } from '../shared/applied-truth-transport';
import { normalizePhpListish } from '../shared/php-listish.js';
import { awaitEntityBaseReady, awaitEntityActionReady } from '../shared/entity-compile-ready';
// status-truth.ts — Lane GMS (2026-09-27): the client-truth GM STATUS-TOOLS adapter + WS subscriber.
//
// GM effects use gm-boundary-truth: browser resolution and consent, then a raw status_truth_commit save.
// Toggle and pause commands retain their shared actor outbox and reconciliation.
//
// Reconciliation is per SECTION (S1: the row's `triggers` bucket): each section remembers the revision that last
// confirmed it, so a late answer / correction never overwrites a newer confirmed value, and a newer pending operation of
// ours keeps its optimistic value until its own answer lands.
import {
  applyCreatureState, applyStatusAdded, applyStatusTimeFields, applyTriggerRows, resolveCreatureLife, resolveStatusAdd, resolveStatusDelete, resolveStatusPause, resolveTriggerClear, resolveTriggerToggle, STATUS_TRUTH_SCHEMA,
  statusEntry, statusTimeFields, statusIsPaused, triggerRows,
  type StatusTimeFields, type StatusTruthKind,
} from '../janus-engine/statuses/gms-tools.js';
import {
  MovementTruthRejected, MovementTruthTerminal,
  type ActorTruthCommandBase, type MovementTruthAcceptedResult, type MovementTruthRejectedResult, type MovementTruthRow, type MovementTruthTerminalInfo,
} from '../janus-engine/movement/client-truth-commit.js';
import { encounterField, type CasterArr } from '../shared/array-types.js';
import { _ftCamField } from '../shared/campaign-field.js';
import { ftCampaignNowSeconds, ftCampaignTimeAddSeconds, ftClockCalendarFromArr, ftClockColsFromArr, type CampaignClockCols } from '../shared/campaign-clock.js';
import { abilityTruthCampaign } from './ability-truth.js';
import { classifyRefusal } from '../shared/revert-on-refusal.js';
import { ensureActorRowHeld, runRowOnly } from '../shared/actor-row-loader.js';
import { handleCasterUpdate } from './caster-update.js';
import { fetchAndCacheObject } from './object-cache.js';
import { commitGmBoundary, commitGmSkip, planGmInventoryAdd, settleGmBoundary } from './gm-boundary-truth.js';
import './placed-item-trap';
import { withoutEncumbranceApplication } from '../janus-engine/compile/encumbrance-application-plans';
import { refuseUnported, skipUnported } from '../janus-engine/unported.js';
import { itemField } from '../janus-engine/ability/context.js';
import { statusQuantityAlias } from '../janus-engine/ability/statuses/inventory-status-applicability.js';
import { planTrapMount, type TrapMountInputs } from '../janus-engine/traps/mount';
import { checkInventoryEnchantment } from '../shared/enchantment-validation-client';
import { statusRows } from '../janus-engine/statuses/expiry.js';
import { retireEncounterCreatures } from './creature-departure.js';
import { LONG_REST_IDS } from '../janus-engine/turns/long-rest.js';
import { buildAppliedFrame } from '../janus-engine/transport/applied-frame';
import { changeCraftMembership, startCraft } from './crafting-truth.js';
import './book-truth.js';
import {
  actorEncounterFor, actorOutbox, actorRowFor, admitProvisional, bankConfirmedRev, confirmedRevFor,
  noteFrameSeen, noteOwnOperation, pendingFor, reconcileActorRow, registerTruthSchema, waitForActorMovement,
} from './movement-truth.js';

type Row = Record<string, unknown>;
const asRow = (value: unknown): Row | null => value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Row : null;
const upper = (value: unknown): string => String(value ?? '').trim().toUpperCase();
const ENDPOINT = '/options/game/actions/status_truth_commit_inc.php';

/** The row sections a frame governs: `triggers` (S1) and `status:<status_id>` — one status entry's pause / end columns (S3). */
type Section = string;
export interface StatusTruthFrame {
  triggers?: Row[];
  status_fields?: { status_id: string; fields: StatusTimeFields; deleted?: number };
  /** S4: an added row — the provisional id the optimistic entry used, and the saved entry (null = not applied). */
  status_added?: { provisional_id: string; entry: Row | null };
  /** S5 / S6: a removed row — null once gone; its saved lean entry while it still lives (a correction). */
  status_removed?: { status_id: string; entry: Row | null };
  /** S9: the actor's life state after a GM kill / resurrect / destroy (dead flag; a destroyed row leaves with the fan-out). */
  creature_state?: { dead: number; deleted?: number; v_current?: number };
}
export interface StatusTruthCommand extends ActorTruthCommandBase {
  kind: StatusTruthKind; roll_private: number; ability_id?: string | null; status_id?: string | null;
  before: ActorTruthCommandBase['before'] & Record<string, unknown>;
  after: Record<string, unknown> & StatusTruthFrame;
}
const cmd = (command: ActorTruthCommandBase): StatusTruthCommand => command as StatusTruthCommand;

const clone = <T>(value: T): T => JSON.parse(JSON.stringify(value ?? null)) as T;
/** The sections a frame carries, with their values. */
function frameSections(frame: StatusTruthFrame | null | undefined): Array<[Section, unknown]> {
  const out: Array<[Section, unknown]> = [];
  if (!frame) return out;
  if (frame.triggers) out.push(['triggers', normalizePhpListish(frame.triggers)]);
  const sf = asRow(frame.status_fields);
  if (sf && typeof sf.status_id === 'string' && asRow(sf.fields) && !sf.deleted) out.push(['status:' + upper(sf.status_id), sf.fields]);
  const sa = asRow(frame.status_added);
  if (sa && typeof sa.provisional_id === 'string') out.push(['add:' + upper(sa.provisional_id), asRow(sa.entry)]);
  const sr = asRow(frame.status_removed);
  if (sr && typeof sr.status_id === 'string') out.push(['del:' + upper(sr.status_id), asRow(sr.entry)]);
  if (asRow(frame.creature_state)) out.push(['life', frame.creature_state]);
  return out;
}
/** The sections a command governs (its own writes). */
function sectionsFor(command: StatusTruthCommand): Section[] {
  if (command.kind === 'trigger_toggle' || command.kind === 'trigger_clear') return ['triggers'];
  if ((command.kind === 'status_pause' || command.kind === 'status_resume') && command.status_id) return ['status:' + upper(command.status_id)];
  const pid = asRow(command.after?.status_added)?.provisional_id;
  if (command.kind === 'status_add' && typeof pid === 'string') return ['add:' + upper(pid)];
  if (command.kind === 'status_delete' && command.status_id) return ['del:' + upper(command.status_id)];
  if (command.kind === 'creature_kill' || command.kind === 'creature_resurrect' || command.kind === 'creature_destroy' || command.kind === 'creature_flee') return ['life'];
  return [];
}
function sectionOf(row: Row, section: Section): unknown {
  if (section === 'triggers') return triggerRows(row);
  if (section.startsWith('status:')) return statusTimeFields(row, section.slice(7));
  if (section.startsWith('add:') || section.startsWith('del:')) { const e = statusEntry(row, section.slice(4)); return e ? { ...e } : null; }
  if (section === 'life') return { dead: Number((asRow(row.encounter_creatures)?.encounter_creature_dead as number | undefined) ?? 0) === 1 ? 1 : 0 };
  return null;
}
function applySection(row: Row, section: Section, value: unknown): void {
  if (section === 'triggers') applyTriggerRows(row, value);
  else if (section.startsWith('status:') && value) applyStatusTimeFields(row, section.slice(7), value);
  else if (section.startsWith('add:') || section.startsWith('del:')) applyStatusAdded(row, section.slice(4), asRow(value), section.startsWith('add:')); // null = remove; an entry = (re)installed (an add leads — N4e)
  else if (section === 'life') applyCreatureState(row, value);
}

interface Pending { prior: Partial<Record<Section, unknown>>; sections: readonly Section[]; encounterId: string; actorId: string; rev: number;
  /** 2026-09-30 (Compiler IV): a DELETE's exact prior placement — the row's entry in BOTH lists and its index in each. applyStatusAdded
   *  re-adds a restored row to `caster` only when it lacks status_caster_id, so a self-cast row the server stamps WITH one came back without its
   *  caster copy after a 422 hand-back (item-tail: Preemptive Strike), and the legacy claim built next read `stale:fingerprint`. */
  placement?: Record<string, { target: { entry: unknown; at: number } | null; caster: { entry: unknown; at: number } | null }> }
const pending = new Map<string, Pending>();
const actorKey = (encounterId: string, actorId: string): string => upper(encounterId) + ':' + upper(actorId);
/** Per-actor, PER-SECTION confirmed truth (the revision that last confirmed it + its value). */
const confirmedSections = new Map<string, Partial<Record<Section, { rev: number; value: unknown }>>>();
function newerPendingSections(command: ActorTruthCommandBase, rev: number): Set<Section> {
  const out = new Set<Section>();
  for (const p of pending.values()) if (p.encounterId === upper(command.encounter_id) && p.actorId === upper(command.actor_id) && p.rev > rev) p.sections.forEach(s => out.add(s));
  return out;
}
/** Saved truth at `rev` onto the row: only sections that are neither superseded nor rewritten by a newer pending op. */
function installSaved(command: ActorTruthCommandBase, row: Row, rev: number, frame: StatusTruthFrame): void {
  const key = actorKey(command.encounter_id, command.actor_id);
  const known = confirmedSections.get(key) ?? {};
  const blocked = newerPendingSections(command, rev);
  for (const [section, value] of frameSections(frame)) {
    if (known[section] && known[section]!.rev > rev) continue;
    // An add's saved row shares its freshness with that row's delete (Codex S9 review F8): a delayed add acknowledgement must
    // not resurrect a row a NEWER delete already confirmed gone.
    const addedId = section.startsWith('add:') ? upper(asRow(value)?.status_id) : '';
    if (addedId && known['del:' + addedId] && known['del:' + addedId]!.rev > rev) { known[section] = { rev, value: null }; applySection(row, section, null); continue; }
    known[section] = { rev, value: clone(value) };
    if (!blocked.has(section)) applySection(row, section, value);
  }
  confirmedSections.set(key, known);
  if (confirmedSections.size > 512) confirmedSections.delete(confirmedSections.keys().next().value!);
}
/** The row's entry + index in each status list (null when the list does not hold it). */
function statusPlacement(row: Row, statusId: string): { target: { entry: unknown; at: number } | null; caster: { entry: unknown; at: number } | null } {
  const find = (mode: 'target' | 'caster') => { const list = statusRows(row, mode);
    const at = list.findIndex(e => upper(asRow(e)?.status_id) === upper(statusId)); return at < 0 ? null : { entry: clone(list[at]), at }; };
  return { target: find('target'), caster: find('caster') };
}
/** Put a deleted row back EXACTLY where it was, in both lists (the restore after a refused / handed-back optimistic delete). */
function restorePlacement(row: Row, statusId: string, placed: NonNullable<Pending['placement']>[string], saved: unknown = null): void {
  const statuses = (row.statuses ??= {}) as Row;
  for (const mode of ['target', 'caster'] as const) {
    const current = statusRows(row, mode);
    // a list that holds the row AGAIN (a newer frame re-installed it — its pause / stack fields are newer than this snapshot) is left as it
    // is (Codex review 2026-09-30); only a list the optimistic delete emptied of it gets the snapshot back, at its old index
    if (current.some(e => upper(asRow(e)?.status_id) === upper(statusId))) continue;
    const was = placed[mode];
    if (!was) continue;
    // a confirmed saved entry may carry a newer STACK (the only row field a delete's confirmation can move); the rest is the held row
    const stack = asRow(saved)?.status_stack;
    const list = [...current];
    list.splice(Math.min(was.at, list.length), 0, { ...(clone(was.entry) as Row), ...(stack !== undefined ? { status_stack: stack } : {}) });
    statuses[mode] = list;
  }
}
function restoreOwn(command: ActorTruthCommandBase, row: Row, own: Pending, handled: Section[] = []): void {
  const blocked = newerPendingSections(command, own.rev);
  const known = confirmedSections.get(actorKey(command.encounter_id, command.actor_id)) ?? {};
  // A CONFIRMED null means "gone" — only a section with no confirmation at all falls back to the prior value (Codex S9 review F9).
  for (const section of own.sections) {
    if (blocked.has(section) || handled.includes(section)) continue;
    const value = known[section] ? known[section]!.value : own.prior[section];
    // a DELETE whose row is (confirmed) still present goes back to its exact pre-delete placement in both lists
    const placed = section.startsWith('del:') && value ? own.placement?.[section.slice(4)] : undefined;
    if (placed) restorePlacement(row, section.slice(4), placed, known[section] ? value : null);
    else applySection(row, section, value);
  }
}
function render(command: ActorTruthCommandBase, row: MovementTruthRow): void {
  handleCasterUpdate({ type: 'caster_update', caster_arr: row as unknown as CasterArr, local: true });
  const c = cmd(command);
  if (pending.has(command.operation_id) && (c.kind === 'status_pause' || c.kind === 'status_resume') && c.after.status_fields) {
    // Keep the held raw rows and memo at the same revision as the painted clock.
    // Otherwise the next GM delete can arrive while the save echo recompiles it.
    const rev = command.expected_rev + 1;
    const applied_frame = buildAppliedFrame({ operation_id: command.operation_id, encounter_id: command.encounter_id,
      actor_id: command.actor_id, rev, member_revs: { [command.actor_id]: rev },
      rows: [
        { table: 'encounter_creatures', row_id: command.actor_id, partial: true, row: { movement_rev: rev } },
        { table: 'statuses', row_id: c.after.status_fields.status_id, partial: true,
          row: { status_id: c.after.status_fields.status_id, ...c.after.status_fields.fields } },
      ], computed: {}, computed_delta: { [command.actor_id]: { set: [], unset: [] } }, new_entities: [], deletes: [] }, Infinity);
    window.ftPushAppliedTruth?.({ type: 'janus_status_truth', command: { ...command, rev }, applied_frame });
  }
  window.dispatchEvent(new CustomEvent('ft:status-truth-applied', { detail: { command } }));
}
function settleShared(command: ActorTruthCommandBase, row: MovementTruthRow): void {
  if (pendingFor(command.encounter_id, command.actor_id) === 0) reconcileActorRow(command.encounter_id, command.actor_id, row);
}

/** POST with a 20 s budget for the answer HEADERS only (resource-truth.ts: a body-read abort turned saved commits into failures). */
async function postJson(body: { operation_id: string }): Promise<Response> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(new DOMException('signal timed out', 'TimeoutError')), 20000);
  try { return await saveAppliedTruth(ENDPOINT, body, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body), signal: controller.signal }); }
  finally { clearTimeout(timer); }
}
async function post(command: ActorTruthCommandBase): Promise<unknown> {
  withLogParticipants(command);
  const response = await postJson(command);
  let result: Row | null = null;
  try { result = await response.json() as Row; } catch { result = null; }
  if (!response.ok && result?.error === 'stale' && Number.isInteger(result.rev)) {
    const row = actorRowFor(command.encounter_id, command.actor_id);
    const position = { x_pos: Number(row?.position?.x_pos ?? 0), y_pos: Number(row?.position?.y_pos ?? 0), z_pos: Number(row?.position?.z_pos ?? 0) };
    throw new MovementTruthRejected({ ok: false, error: 'stale', rev: Number(result.rev), position, frame: asRow(result.frame) ?? {} } as unknown as MovementTruthRejectedResult);
  }
  if (response.ok && result?.ok === true) return result;
  const message = String(result?.error ?? 'The status change could not be saved.');
  if (response.status >= 400 && response.status < 500) throw new MovementTruthTerminal(response.status, message);
  throw new Error(message);
}
function accepted(command: ActorTruthCommandBase, result: MovementTruthAcceptedResult): void {
  const row = actorRowFor(command.encounter_id, command.actor_id);
  pending.delete(command.operation_id);
  const res = result as unknown as Row;
  if (!row) return;
  bankConfirmedRev(command.encounter_id, command.actor_id, row, result.rev);
  const saved = asRow((result as unknown as Row).frame) as StatusTruthFrame | null;
  if (saved) installSaved(command, row as Row, result.rev, saved);
  settleShared(command, row);
  render(command, row);
}
function rejected(command: ActorTruthCommandBase, result: MovementTruthRejectedResult): void {
  const row = actorRowFor(command.encounter_id, command.actor_id);
  const own = pending.get(command.operation_id);
  pending.delete(command.operation_id);
  if (row) {
    const saved = (asRow((result as unknown as Row).frame) ?? {}) as StatusTruthFrame;
    bankConfirmedRev(command.encounter_id, command.actor_id, row, result.rev);
    installSaved(command, row as Row, result.rev, saved);
    if (own) restoreOwn(command, row as Row, own, frameSections(saved).map(([section]) => section));
    reconcileActorRow(command.encounter_id, command.actor_id, row);
    render(command, row);
  }
  window.dispatchEvent(new CustomEvent('ft:status-truth-reconciled', { detail: { command, authoritative: { rev: result.rev } } }));
}
/** Crafting controls await the durable activity save. */
export interface StatusTruthAnswer { ok: boolean; status?: number; error?: string; refused?: string; notice?: string }
function failed(command: ActorTruthCommandBase, info: MovementTruthTerminalInfo): void {
  const row = actorRowFor(command.encounter_id, command.actor_id);
  const own = pending.get(command.operation_id);
  pending.delete(command.operation_id);
  if (row) {
    if (own) restoreOwn(command, row as Row, own);
    reconcileActorRow(command.encounter_id, command.actor_id, row);
    render(command, row);
  }
  if (typeof displayWarning === 'function') displayWarning(info.status === 403 ? 'You do not have permission to change this creature.' : 'The change was refused: ' + info.error);
  window.dispatchEvent(new CustomEvent('ft:status-truth-refused', { detail: { command, info, refusal: classifyRefusal({ status: info.status, body: { error: info.error } }) } }));
}
registerTruthSchema(STATUS_TRUTH_SCHEMA, {
  apply: (row, command, rev) => {
    const c = cmd(command);
    const sections = sectionsFor(c);
    const prior: Partial<Record<Section, unknown>> = {};
    const key = actorKey(command.encounter_id, command.actor_id);
    const known = confirmedSections.get(key) ?? {};
    const base = confirmedRevFor(command.encounter_id, command.actor_id, row);
    const ownPending = newerPendingSections(command, -1); // sections an earlier op of ours still holds optimistically
    for (const section of sections) {
      prior[section] = clone(sectionOf(row as Row, section));
      // First sight — or a NEWER confirmed install since (a legacy caster_update / snapshot advanced the shared watermark;
      // Codex S1 review F6): with nothing of ours pending on the section, the row's value IS confirmed truth at `base`.
      if (!known[section] || (known[section]!.rev <= base && !ownPending.has(section))) known[section] = { rev: base, value: clone(prior[section]) };
    }
    confirmedSections.set(key, known);
    const placement: NonNullable<Pending['placement']> = {};
    for (const section of sections) if (section.startsWith('del:')) placement[section.slice(4)] = statusPlacement(row as Row, section.slice(4));
    pending.set(command.operation_id, { prior, sections, encounterId: upper(command.encounter_id), actorId: upper(command.actor_id), rev, placement });
    if (pending.size > 256) pending.delete(pending.keys().next().value!);
    for (const [section, value] of frameSections(c.after)) if (sections.includes(section)) applySection(row as Row, section, value);
    row.encounter_creatures.movement_rev = rev;
  },
  render, commit: post, accepted, rejected, failed,
  staleMessage: 'Your change was rejected: this creature changed elsewhere. Its current state was restored.',
});

/** The encounter holding the actor: the hint, the page's active encounter, then any cached encounter. */
function encounterIdFor(actorId: string, hint?: string): string | null {
  const ids = [hint, window.ftGetActiveEncounterId?.()].map(upper).filter(Boolean);
  for (const id of ids) if (actorRowFor(id, actorId)) return id;
  const cache = (window as unknown as { FateTide?: { encounterCache?: { keys?: () => Iterable<string> } } }).FateTide?.encounterCache;
  for (const key of cache?.keys?.() ?? []) if (actorRowFor(upper(key), actorId)) return upper(key);
  return null;
}

export type StatusCommitOutcome = 'handled' | 'refused' | null;
function enqueue(encounterId: string, actorId: string, row: MovementTruthRow, command: StatusTruthCommand): StatusCommitOutcome {
  try {
    actorOutbox(encounterId, actorId).enqueue(withLogParticipants(command));
  } catch (error) {
    const own = pending.get(command.operation_id);
    pending.delete(command.operation_id);
    if (own) {
      for (const section of own.sections) { const placed = section.startsWith('del:') ? own.placement?.[section.slice(4)] : undefined;
        if (placed) restorePlacement(row as Row, section.slice(4), placed); else applySection(row as Row, section, own.prior[section]); }
      row.encounter_creatures.movement_rev = command.expected_rev; render(command, row);
    }
    if (typeof displayWarning === 'function') displayWarning('The change could not be committed: ' + (error instanceof Error ? error.message : String(error)));
    return 'refused';
  }
  noteOwnOperation(command.operation_id, STATUS_TRUTH_SCHEMA);
  return 'handled';
}
/** Join recovery before taking the page's current actor and encounter snapshot. */
async function held(actorIdRaw: string, encounterHint?: string): Promise<{ encounterId: string; actorId: string; row: MovementTruthRow } | null> {
  const actorId = upper(actorIdRaw);
  if (!actorId || !await awaitEntityBaseReady([actorId, ...(encounterHint ? [encounterHint] : [])])) return null;
  const encounterId = encounterIdFor(actorId, encounterHint);
  if (!encounterId || !await awaitEntityBaseReady([encounterId, actorId]) || !actorEncounterFor(encounterId)) return null;
  const row = actorRowFor(encounterId, actorId);
  if (!row) return null;
  confirmedRevFor(encounterId, actorId, row);
  return { encounterId, actorId, row };
}
const base = (h: { encounterId: string; actorId: string; row: MovementTruthRow }, kind: StatusTruthKind) => ({
  schema: STATUS_TRUTH_SCHEMA, operation_id: crypto.randomUUID().toUpperCase(), encounter_id: h.encounterId, actor_id: h.actorId,
  expected_rev: Number(h.row.encounter_creatures.movement_rev ?? 0), kind, roll_private: 0,
});

/** S1 — the trigger badge; an unsupported toggle is refused locally. */
export async function tryCommitTriggerToggle(input: { abilityId: string; actorId: string; encounterId?: string; inactive: unknown }): Promise<StatusCommitOutcome> {
  const h = await held(input.actorId, input.encounterId);
  if (!h) return null;
  const res = resolveTriggerToggle(h.row as Row, { actorId: h.actorId, abilityId: input.abilityId, inactive: input.inactive, uuid: () => crypto.randomUUID().toUpperCase() });
  if (!res.supported) {
    if (res.refused) { if (typeof displayWarning === 'function') displayWarning(res.message); return 'refused'; }
    window.dispatchEvent(new CustomEvent('ft:status-slice-unavailable', { detail: { kind: 'trigger_toggle', reason: res.reason } }));
    return null;
  }
  const command: StatusTruthCommand = { ...base(h, 'trigger_toggle'), ability_id: upper(input.abilityId),
    before: res.before as unknown as StatusTruthCommand['before'], after: { trigger_id: res.after.trigger_id, trigger_inactive: res.after.trigger_inactive, triggers: res.after.triggers } };
  return enqueue(h.encounterId, h.actorId, h.row, command);
}
/** S1 — Clear Triggers (clear_triggers_for_caster_inc.php). */
export async function tryCommitTriggerClear(input: { actorId: string; encounterId?: string }): Promise<StatusCommitOutcome> {
  const h = await held(input.actorId, input.encounterId);
  if (!h) return null;
  const res = resolveTriggerClear(h.row as Row, { actorId: h.actorId });
  if (!res.supported) {
    if (res.refused) { if (typeof displayWarning === 'function') displayWarning(res.message); return 'refused'; }
    window.dispatchEvent(new CustomEvent('ft:status-slice-unavailable', { detail: { kind: 'trigger_clear', reason: res.reason } }));
    return null;
  }
  const command: StatusTruthCommand = { ...base(h, 'trigger_clear'), before: res.before as unknown as StatusTruthCommand['before'], after: { triggers: [] } };
  return enqueue(h.encounterId, h.actorId, h.row, command);
}

/** The encounter row holding a status row as a TARGET status (its creature), or null when the page holds none. */
async function statusHolder(statusId: string): Promise<{ encounterId: string; actorId: string; row: MovementTruthRow } | null> {
  const want = upper(statusId);
  const cache = (window as unknown as { FateTide?: { encounterCache?: Map<string, unknown> } }).FateTide?.encounterCache;
  const ids = [window.ftGetActiveEncounterId?.(), ...(cache?.keys?.() ?? [])].map(upper).filter(Boolean);
  for (const encounterId of new Set(ids)) {
    if (!await awaitEntityBaseReady([encounterId])) continue;
    const roster = normalizePhpListish(asRow(cache?.get(encounterId))?.encounter_creatures_arr);
    for (const entry of roster) {
      const row = asRow(entry) as MovementTruthRow | null;
      if (!row) continue;
      if (statusRows(row as Row, 'target').some(entry => upper(entry.status_id) === want)) {
        const actorId = upper(ftEncounterCreatureField(row.encounter_creatures, 'encounter_creature_id'));
        const current = actorId ? await held(actorId, encounterId) : null;
        if (current && statusRows(current.row as Row, 'target').some(entry => upper(entry.status_id) === want)) return current;
      }
    }
  }
  return null;
}
/**
 * N6b (2026-10-01, R-065): the status's holder for the status-id tools (pause / resume / delete). The page's own row first; else the tiny
 * status-row read (status_holder_read_inc.php — the target creature + its encounter, authorized from the rows) and the shared actor-row
 * loader fetches that encounter into the page. Bounded: one read, one encounter load. N6e (coordinator Codex review round 5 T2): an
 * INVENTORY-held status comes back named as such (InventoryStatusHolder); every other non-holder — the read's own 404 (MISSING), a holder
 * the page cannot load (UNLOADABLE), a failed read (UNKNOWN) — is refused with words by the tool (refuseUnlocated), never the legacy route.
 * N6d (2026-10-02, owner ruling 2026-10-01 — no actor is controlled outside an encounter): ENCOUNTERLESS = the status's holder is a
 * creature in no live encounter (the read says so): every status-id tool refuses it with words (`refuseEncounterless`), never legacy.
 */
const ENCOUNTERLESS = 'encounterless' as const;
/** N6d (N6d Codex review P1): the read itself failed (no answer, a 5xx, a malformed body) — the holder is UNKNOWN, so the tool is refused
 *  with words (it might be a creature in no encounter); only the read's own 404 (an inventory-held / unreadable status) keeps the route. */
const UNKNOWN = 'unknown' as const;
/** N6e (coordinator Codex review round 5 T2 P2): the read's OWN 404 (its JSON body — a proxy's / a missing route's HTML 404 is UNKNOWN): the
 *  status is gone or the viewer may not read its holder → words. */
const MISSING = 'missing' as const;
/** N6e (round 5 T2 P2): the read named a creature in an encounter, but the page could not load it (or the loaded row lacks the status) → words. */
const UNLOADABLE = 'unloadable' as const;
type StatusHolder = { encounterId: string; actorId: string; row: MovementTruthRow };
/** N6e: an INVENTORY-held status, named as such by the read — its row, and the creature holding that row (null = an encounter floor). */
interface InventoryStatusHolder { inventory: true; inventoryId: string; actorId: string | null; encounterId: string; statusItemId: string }
type LocatedStatus = StatusHolder | InventoryStatusHolder | typeof ENCOUNTERLESS | typeof UNKNOWN | typeof MISSING | typeof UNLOADABLE;
const UUID_RE = /^[0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12}$/;
/** The holder read itself (status_holder_read_inc.php): its 2xx answer, or MISSING (its own JSON 404) / UNKNOWN (anything else). */
async function holderRead(body: Row): Promise<Row | typeof MISSING | typeof UNKNOWN> {
  let answer: Row | null = null;
  try {
    const res = await fetch('/options/game/actions/status_holder_read_inc.php', { method: 'POST', credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body), signal: AbortSignal.timeout(10000) });
    answer = asRow(await res.json().catch(() => null));
    if (res.status === 404) return answer?.ok === false && answer.error === 'Not found' ? MISSING : UNKNOWN;
    if (!res.ok || !answer) return UNKNOWN;
  } catch { return UNKNOWN; }
  return answer.ok === true ? answer : UNKNOWN; // a 2xx that is not the read's answer (N6d review round 2 P1)
}
/** An item holder answer → InventoryStatusHolder (UNKNOWN when malformed). */
function itemHolderOf(answer: Row): InventoryStatusHolder | typeof UNKNOWN {
  const inventoryId = upper(answer.inventory_id); const encounterId = upper(answer.encounter_id); const actorId = upper(answer.actor_id);
  if (!UUID_RE.test(inventoryId) || !UUID_RE.test(encounterId) || (actorId !== '' && !UUID_RE.test(actorId))) return UNKNOWN;
  return { inventory: true, inventoryId, actorId: actorId || null, encounterId, statusItemId: upper(answer.status_item_id) };
}
/** N6e: is `targetId` an ITEM (a status add on it)? Its holder, or MISSING (not an item — a creature, or nothing the viewer may read). */
async function locateItemTarget(targetId: string): Promise<InventoryStatusHolder | typeof ENCOUNTERLESS | typeof UNKNOWN | typeof MISSING> {
  const id = upper(targetId);
  if (!UUID_RE.test(id)) return MISSING;
  const answer = await holderRead({ inventory_id: id });
  if (typeof answer === 'string') return answer;
  if (answer.encounterless === true) return ENCOUNTERLESS;
  return answer.holder === 'inventory' ? itemHolderOf(answer) : UNKNOWN;
}
async function locateStatusHolder(statusId: string): Promise<LocatedStatus> {
  const own = await statusHolder(statusId);
  if (own) return own;
  const id = upper(statusId);
  if (!UUID_RE.test(id)) return MISSING;
  const answer = await holderRead({ status_id: id });
  if (typeof answer === 'string') return answer;
  if (answer.encounterless === true) return ENCOUNTERLESS;
  if (answer.holder === 'inventory') return itemHolderOf(answer);
  const where = await ensureActorRowHeld(answer.actor_id, answer.encounter_id, { what: 'The status' });
  if (where.kind !== 'encounter') return UNLOADABLE;
  return await statusHolder(id) ?? UNLOADABLE;
}
const isCreatureHolder = (h: LocatedStatus): h is StatusHolder => typeof h === 'object' && !('inventory' in h);
const isInventoryHolder = (h: LocatedStatus): h is InventoryStatusHolder => typeof h === 'object' && 'inventory' in h;
/** N6e: the worded refusal for every holder a status tool cannot act on — never the legacy endpoint. */
function refuseUnlocated(h: Exclude<LocatedStatus, StatusHolder | InventoryStatusHolder>, verb: string): 'refused' {
  if (h === ENCOUNTERLESS) return refuseEncounterless(verb);
  if (h === MISSING) return statusWords(`The status was not ${verb}: it was not found (it may already be gone, or you cannot see its creature). Reload the page.`);
  if (h === UNLOADABLE) return refuseUnloadable(verb);
  return refuseUnknown(verb);
}
/** N6e: the status's creature could not be loaded on this page (or no longer carries it). */
function refuseUnloadable(verb: string): 'refused' {
  return statusWords(`The status was not ${verb}: this page could not load the creature that holds it. Reload the page and try again.`);
}
/**
 * N6e (2026-10-02, R-065): a status held by an ITEM changes through the status commit's inventory-holder mode (status_truth_inventory.php —
 * hide / reveal / delete / add). An item's statuses ride no actor outbox: no optimistic draw, the post is retried under the SAME operation id
 * on a lost answer (the commit keeps a receipt), and the page repaints from the legacy fan-out's updates. The rows are the authority (the
 * item's root holder); `actorId` names that root (the encounter id for an item on the floor).
 */
async function commitInventoryStatus(h: InventoryStatusHolder, kind: StatusTruthKind, verb: string, extra: Row): Promise<{ outcome: 'handled' | 'refused'; frame: Row | null }> {
  const body = { schema: STATUS_TRUTH_SCHEMA, operation_id: crypto.randomUUID().toUpperCase(), encounter_id: h.encounterId, actor_id: h.actorId ?? h.encounterId,
    expected_rev: 0, kind, roll_private: 0, holder: 'inventory', inventory_id: h.inventoryId, before: {}, after: {}, ...extra };
  let lastError = '';
  const deadline = Date.now() + 30000;
  for (let attempt = 0; attempt < 3; attempt++) {
    const remaining = deadline - Date.now();
    if (remaining <= 0) break;
    let response: Response;
    let result: Row | null;
    try {
      response = await fetch(ENDPOINT, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
        signal: AbortSignal.timeout(Math.min(10000, remaining)) });
      result = await response.json() as Row;
    } catch (error) { lastError = error instanceof Error ? error.message : String(error); continue; }
    if (response.ok && result?.ok === true) {
      window.dispatchEvent(new CustomEvent('ft:status-truth-inventory', { detail: { kind, inventoryId: h.inventoryId, frame: result.frame ?? null } }));
      return { outcome: 'handled', frame: asRow(result.frame) };
    }
    if (response.status >= 500 && attempt < 2) { lastError = String(result?.error ?? response.status); await new Promise<void>(r => setTimeout(r, 400)); continue; }
    const why = String(result?.error ?? response.status);
    const held = asRow(result?.frame)?.held === 1 || asRow(asRow(result?.authoritative)?.frame)?.held === 1;
    const words = response.status === 403 ? `The status was not ${verb}: you do not have permission to change this item.`
      : response.status === 409 && kind === 'status_add' && held ? `The status was not ${verb}: the item already has it.`
      : response.status === 409 ? `The status was not ${verb}: the item changed elsewhere. Reload the page and try again.`
      : response.status === 404 ? `The status was not ${verb}: it was not found (it may already be gone). Reload the page.`
      : response.status === 422 ? `The status was not ${verb}: ${why}`
      : `The status was not ${verb} (${why}).`;
    statusWords(words);
    return { outcome: 'refused', frame: null };
  }
  statusWords(`The status change could not be confirmed (${lastError}). Check the item before trying again.`);
  return { outcome: 'refused', frame: null };
}
/** N6e: an item's status cannot be paused, resumed or re-stacked — the legacy endpoints refused an item at their creature gate. */
function refuseOnItem(verb: string): 'refused' {
  return statusWords(`The status was not ${verb}: an item's status cannot be ${verb} — only a creature's.`);
}
/** N6d: the words for a status tool on a creature in no encounter (never posted). */
function refuseEncounterless(verb: string): 'refused' {
  return statusWords(`The status was not ${verb}: its creature is not in an encounter, and creatures outside an encounter are not controlled. ${OUTSIDE}`);
}
/** N6d: the words when the holder could not be checked (the status-row read failed). */
function refuseUnknown(verb: string): 'refused' {
  return statusWords(`The status was not ${verb}: this page could not check which creature holds it. Try again.`);
}
/** Flip every open Hide / Reveal pair for the status (render-status-form.ts + the PHP form share these ids). */
function showHiddenPair(statusId: string, hidden: boolean): void {
  const hide = document.getElementById(`btn_hide_status_effect_${statusId}`);
  const reveal = document.getElementById(`btn_reveal_status_effect_${statusId}`);
  if (hide) hide.style.display = hidden ? 'none' : 'block';
  if (reveal) reveal.style.display = hidden ? 'block' : 'none';
}
/**
 * S2 — Hide / Reveal a status row (hide_status_effect.php / reveal_status_effect.php): an absolute, idempotent set on the
 * status instance that nothing compiled carries, so it rides no outbox and patches no row — the commit authorizes from the
 * status's TARGET row and runs the legacy writer without the recompile. Null = the status's creature could not be located
 * (an inventory-held status, an unreadable target): the caller runs the legacy endpoint. A creature in no encounter is refused
 * with words (N6d).
 * N6c (2026-10-01, R-065): ASYNC — a holder the page does not hold is located through the status-row read + the shared loader
 * (locateStatusHolder), exactly as pause / resume / delete; the callers await it.
 */
/** N6c (Codex review P2): one hide / reveal at a time per status, in CLICK order — the async holder lookup could otherwise let a slow
 *  Hide land after a later Reveal. Admission happens before the first await. */
const hiddenChains = new Map<string, Promise<unknown>>();
/** N6d (coordinator Codex review P2): the LAST CONFIRMED hidden value per status while its chain runs. A refusal (or an answer that never
 *  came) paints THAT back — never the opposite of the requested value, which flipped the pair when a queued Hide then Reveal were both
 *  refused. Seeded at the chain's first admission with the state the page showed before that click (the click asks for the opposite). */
const confirmedHidden = new Map<string, boolean>();
export function tryCommitStatusHidden(input: { statusId: string; hidden: boolean }): Promise<StatusCommitOutcome> {
  const key = upper(input.statusId);
  if (!hiddenChains.has(key)) confirmedHidden.set(key, !input.hidden);
  const prior = hiddenChains.get(key) ?? Promise.resolve();
  const next = prior.then(() => commitStatusHiddenNow(input), () => commitStatusHiddenNow(input));
  hiddenChains.set(key, next);
  void next.finally(() => { if (hiddenChains.get(key) === next) { hiddenChains.delete(key); confirmedHidden.delete(key); } }).catch(() => undefined);
  return next;
}
async function commitStatusHiddenNow(input: { statusId: string; hidden: boolean }): Promise<StatusCommitOutcome> {
  const statusId = upper(input.statusId);
  const verb = input.hidden ? 'hidden' : 'revealed';
  const restore = (): void => showHiddenPair(statusId, confirmedHidden.get(statusId) ?? !input.hidden);
  if (!UUID_RE.test(statusId)) { restore(); return refuseUnlocated(MISSING, verb); }
  const h = await locateStatusHolder(statusId);
  if (isInventoryHolder(h)) {
    // N6e: a status on an ITEM — the commit's inventory-holder mode (hide_status_effect.php / reveal_status_effect.php are deleted).
    const r = await commitInventoryStatus(h, input.hidden ? 'status_hide' : 'status_reveal', verb, { status_id: statusId, after: { hidden: input.hidden ? 1 : 0 } });
    if (r.outcome === 'handled') {
      const saved = Number(r.frame?.hidden ?? (input.hidden ? 1 : 0)) === 1;
      confirmedHidden.set(statusId, saved);
      showHiddenPair(statusId, saved);
      window.dispatchEvent(new CustomEvent('ft:status-truth-hidden', { detail: { statusId, hidden: input.hidden } }));
    } else restore();
    return r.outcome;
  }
  if (!isCreatureHolder(h)) { restore(); return refuseUnlocated(h, verb); }
  const kind: StatusTruthKind = input.hidden ? 'status_hide' : 'status_reveal';
  const body = { schema: STATUS_TRUTH_SCHEMA, operation_id: crypto.randomUUID().toUpperCase(), encounter_id: h.encounterId, actor_id: h.actorId,
    expected_rev: Number(h.row.encounter_creatures.movement_rev ?? 0), kind, roll_private: 0, status_id: statusId, before: {}, after: { hidden: input.hidden ? 1 : 0 } };
  return (async (): Promise<StatusCommitOutcome> => {
    let lastError = '';
    for (let attempt = 0; attempt < 3; attempt++) {
      let response: Response;
      try { response = await postJson(body); } catch (error) { lastError = error instanceof Error ? error.message : String(error); continue; } // same operation id: idempotent retry
      const result = await response.json().catch(() => null) as Row | null;
      if (response.ok && result?.ok === true) {
        const saved = Number(asRow(result.frame)?.hidden ?? (input.hidden ? 1 : 0)) === 1;
        confirmedHidden.set(statusId, saved);
        showHiddenPair(statusId, saved);
        window.dispatchEvent(new CustomEvent('ft:status-truth-hidden', { detail: { statusId, hidden: input.hidden } }));
        return 'handled';
      }
      if (response.status >= 500 && attempt < 2) { lastError = String(result?.error ?? response.status); continue; }
      restore();
      if (typeof displayWarning === 'function') displayWarning(response.status === 403 ? 'You do not have permission to change this status effect.' : 'The change was refused: ' + String(result?.error ?? response.status));
      return 'refused';
    }
    restore(); // never answered: the page shows the last confirmed value (the set is absolute and idempotent — a re-click is safe)
    if (typeof displayWarning === 'function') displayWarning('The change could not be confirmed (' + lastError + '). Check the status before trying again.');
    return 'refused';
  })();
}

/** Resolve this holder's campaign, including its authored calendar; never invent a default calendar. */
async function statusCampaign(encounterId: string): Promise<Row | null> {
  const encounter = asRow(actorEncounterFor(encounterId));
  if (!encounter) return null;
  const held = abilityTruthCampaign(encounter);
  if (held && ftClockCalendarFromArr(held).months.length) return held;
  const id = upper(encounterField(encounter, 'encounter_master_parent_id'));
  if (!id || encounterField(encounter, 'encounter_master_parent_type') !== 'campaign') return held;
  return asRow(await fetchAndCacheObject(id).catch(() => null)) ?? held;
}
function definitionFor(row: Row, id: string): Row | null {
  const full = (entry: unknown): Row | null => { const def = asRow(entry); return def && asRow(def.items) && def.item_layer_types ? def : null; };
  const key = upper(id);
  const ft = (window as unknown as { FateTide?: { objectCache?: Map<string, unknown> } }).FateTide;
  const encounter = asRow(actorEncounterFor(upper(ftEncounterCreatureField(row.encounter_creatures, 'encounter_id'))));
  const base = asRow(asRow(encounter?._entity_base)?.base);
  return full(asRow(row.catalog)?.[key]) ?? full(ft?.objectCache?.get(key))
    ?? full(asRow(encounter?.catalog)?.[key]) ?? full(asRow(base?.catalog)?.[key]);
}
/** Canonical StatusEffect.php ids this lane routes by (status-row.ts carries the crafting id the same way). */
const RESTING_STATUS_ID = '3188FB3B-C860-43D4-9BA0-40364C460385';
const CRAFTING_STATUS_ID = '62ED7394-9C33-D41A-C426-1BC6723C999A';
/**
 * S3 — Pause / Resume a status (pause_status_effect_inc.php / resume_status_effect_inc.php — both DELETED, N6e 2026-10-02). Never
 * null now: 'handled' (queued on the actor outbox), or 'refused' with words (an item's status, an unreadable holder or definition, the
 * legacy refusals). A crafting worker row is judged like any status. Rest resumes its timed row before applying sleep through the shared boundary.
 */
export async function tryCommitStatusPause(input: { statusId: string; action: 'pause' | 'resume' }): Promise<StatusCommitOutcome> {
  const verb = input.action === 'pause' ? 'paused' : 'resumed';
  const holder = await locateStatusHolder(input.statusId); // N6b: loads the holder when the page lacks it (R-065)
  if (isInventoryHolder(holder)) return refuseOnItem(verb); // N6e: legacy parity — pause / resume refused an item at their creature gate
  if (!isCreatureHolder(holder)) return refuseUnlocated(holder, verb); // N6d / N6e: words, never the legacy endpoint
  const h0 = await held(holder.actorId, holder.encounterId);
  if (!h0) return refuseUnloadable(verb);
  // The status's definition: the row catalog, the object cache, else fetched once (object_inc.php) — a legacy-applied status's
  // lean entry carries no definition of its own.
  const entryItem = upper(statusEntry(h0.row as Row, input.statusId)?.status_item_id ?? asRow(asRow(statusEntry(h0.row as Row, input.statusId)?.status_arr)?.items)?.item_id);
  const fetched = entryItem && !definitionFor(h0.row as Row, entryItem) && /^[0-9A-F-]{36}$/.test(entryItem) ? asRow(await fetchAndCacheObject(entryItem)) : null;
  const campaign = await statusCampaign(holder.encounterId);
  const calendar = campaign ? ftClockCalendarFromArr(campaign) : null;
  const clock = campaign ? ftClockColsFromArr(Object.fromEntries(['year', 'month', 'day', 'hour', 'minute', 'second']
    .map(u => ['campaign_time_' + u, _ftCamField(campaign, 'campaign_time_' + u) ?? 0]))) : null;
  const derived = campaign && upper(window.FateTide?.campaign?.id) === upper(_ftCamField(campaign, 'campaign_id'))
    ? (window as unknown as { ftCampaignClock?: { getDerived: () => { cols: CampaignClockCols } | null } }).ftCampaignClock?.getDerived()?.cols : null;
  const h = await held(holder.actorId, holder.encounterId); // the row may have moved on while the definition was fetched
  if (!h) return refuseUnloadable(verb);
  const res = resolveStatusPause(h.row as Row, {
    statusId: input.statusId, action: input.action, definition: id => definitionFor(h.row as Row, id) ?? (upper(id) === entryItem ? fetched : null), layer: (row, key) => ftLayerValue(row, key),
    clock: derived ?? clock,
    timeSeconds: calendar?.months.length ? clock => ftCampaignNowSeconds(clock, calendar) : null,
    timeAddSeconds: calendar?.months.length ? (clock, seconds) => ftCampaignTimeAddSeconds(clock, calendar, seconds) : null,
    restingId: RESTING_STATUS_ID, craftingId: CRAFTING_STATUS_ID,
  });
  if (!res.supported) {
    if (res.refused) { if (typeof displayWarning === 'function') displayWarning(res.message); return 'refused'; }
    // N6e (R-065 — pause_ / resume_status_effect_inc.php are deleted): what the page cannot read is refused with words, never posted.
    window.dispatchEvent(new CustomEvent('ft:status-slice-unavailable', { detail: { kind: 'status_' + input.action, reason: res.reason } }));
    return res.reason === 'definition-missing'
      ? statusWords(`The status was not ${verb}: this page could not load its definition. Reload the page and try again.`)
      : refuseUnloadable(verb);
  }
  const kind: StatusTruthKind = input.action === 'pause' ? 'status_pause' : 'status_resume';
  const command: StatusTruthCommand = { ...base(h, kind), status_id: upper(input.statusId), before: res.before as unknown as StatusTruthCommand['before'], after: res.after };
  const outcome = enqueue(h.encounterId, h.actorId, h.row, command);
  if (outcome !== 'handled' || input.action !== 'resume' || entryItem !== RESTING_STATUS_ID) return outcome;
  try {
    await waitForActorMovement(h.encounterId, h.actorId);
    if (!await awaitEntityActionReady([h.encounterId, h.actorId], h.actorId)) return 'refused';
    const saved = await held(h.actorId, h.encounterId);
    const entry = saved && statusEntry(saved.row as Row, input.statusId);
    const fields = saved && statusTimeFields(saved.row as Row, input.statusId);
    // A refused time save restores the paused row. Never put that creature to sleep.
    if (!entry || !fields || statusIsPaused(entry)) return 'refused';
    return await commitGmBoundary(h.encounterId, h.actorId, { kind: 'add', targetId: h.actorId, casterId: h.actorId,
      itemId: LONG_REST_IDS.sleeping, durationInt: '', durationType: '', allowNoChange: true,
      ends: { ends: (['year', 'month', 'day', 'hour', 'minute', 'second'] as const).map(u => fields[`status_ends_campaign_${u}`] ?? 0).join('-'), flags: [0, 0, 0, 0] } });
  } catch (error) { return statusWords('The resumed rest could not apply Sleeping: ' + (error instanceof Error ? error.message : String(error))); }
}

const CRAFTING_PROGRESS_STATUS_ID = 'E49AA8BA-1F60-4FD8-B3C0-852BBA3978C3';
export async function tryCommitStatusAdd(input: { targetId: string; statusItemId: string; casterId?: string; originId?: string; durationInt: unknown; durationType: string; onStale?: () => void;
  rollPrivate?: number; encounterId?: string; c1Retried?: boolean; background?: boolean; stillCurrent?: () => boolean }): Promise<StatusCommitOutcome> {
  if (input.stillCurrent && !input.stillCurrent()) return 'refused';
  if (!await awaitEntityActionReady([input.targetId, ...(input.encounterId ? [input.encounterId] : []), ...(input.casterId ? [input.casterId] : [])])) return 'refused';
  let h = await held(input.targetId, input.encounterId);
  if (input.background && !h) return 'refused';
  if (!h) {
    // N6e: an ITEM target — the holder read names it (no creature row has its id).
    const item = await locateItemTarget(input.targetId);
    if (isInventoryHolder(item)) {
      try {
        const itemId = upper(input.statusItemId);
        const definition = asRow(await fetchAndCacheObject(itemId));
        if (!definition) return statusWords('The status was not added: its definition could not be loaded.');
        const mountTrap = itemField(definition, 'item_type') === 'trap_ability';
        const where = item.actorId ? await ensureActorRowHeld(item.actorId, item.encounterId, { what: 'The item status' }) : null;
        if (where?.kind === 'refused') return statusWords(where.message);
        if (!actorEncounterFor(item.encounterId)) await window.ftResyncEncounter?.(item.encounterId);
        if (mountTrap) {
          const host = asRow(await fetchAndCacheObject(item.inventoryId));
          if (!host) return statusWords('trap-mount:placed-object-facts-missing');
          const response = await fetch('/options/api/trap_mount_inputs_inc.php', { method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ inventory_id: item.inventoryId, ability_id: itemId }), signal: AbortSignal.timeout(10000) });
          if (!response.ok) return statusWords('trap-mount:saved-facts-unavailable');
          const facts = await response.json() as TrapMountInputs & { schema: string };
          if (facts.schema !== 'trap-mount-inputs.v1' || !asRow(facts.inventory) || !Array.isArray(facts.candidates) || !Array.isArray(facts.members))
            return statusWords('trap-mount:saved-facts-unavailable');
          const plan = await planTrapMount(host, definition, facts, { inventoryField: ftInventoryField, value: ftLayerValue,
            entries: (row, key) => ftLayerEntries(row, key) ?? [] }, checkInventoryEnchantment);
          if (!plan.ok) return statusWords(plan.message);
          if (input.stillCurrent && !input.stillCurrent()) return 'refused';
          return (await commitInventoryStatus(item, 'status_add', 'mounted', { trap_mount: plan.command, mount_trap: true, status_item_id: itemId })).outcome;
        }
        const verdict = await planGmInventoryAdd(item.encounterId, item.actorId ?? item.encounterId, item.inventoryId,
          itemId, input.durationInt, input.durationType, input.casterId);
        return (await commitInventoryStatus(item, 'status_add', 'added', { item_boundary: verdict, status_item_id: itemId })).outcome;
      } catch (error) { return statusWords(`The item status was not added: ${error instanceof Error ? error.message : String(error)}`); }
    }
    if (item === ENCOUNTERLESS || item === UNKNOWN) return refuseUnlocated(item, 'added');
    // N6 (2026-10-01): the page does not hold the target creature — load its row / its encounter through the shared actor-row loader.
    // N6d (2026-10-02, owner ruling 2026-10-01): a CREATURE in no encounter is not controlled — refused with words, never the legacy route.
    const where = await ensureActorRowHeld(input.targetId, input.encounterId, { allowRowOnly: true, what: 'The status' });
    if (where.kind === 'encounter') h = await held(where.actorId, where.encounterId);
    else if (where.kind === 'row' && upper(asRow(where.row.encounter_creatures)?.encounter_creature_id) === where.actorId) return refuseEncounterless('added');
    else if (where.kind === 'refused') return statusWords(where.message);
  }
  if (!h) return refuseUnloadable('added');
  // Reuse the encounter's catalog before fetching a definition absent from this page.
  const itemKey = upper(input.statusItemId);
  // Hydrate the family while retaining the incoming alias for the native stack count.
  const definitionKey = statusQuantityAlias(itemKey)?.id ?? itemKey;
  const def = definitionFor(h.row as Row, definitionKey) ?? (/^[0-9A-F-]{36}$/.test(definitionKey) ? asRow(await fetchAndCacheObject(definitionKey)) : null);
  if (input.stillCurrent && !input.stillCurrent()) return 'refused';
  const res = resolveStatusAdd(input.background ? withoutEncumbranceApplication(h.row as Row, itemKey) : h.row as Row,
    { targetId: h.actorId, statusItemId: input.statusItemId, casterId: String(input.casterId ?? ''),
    definition: id => (upper(id) === itemKey ? def : definitionFor(h.row as Row, id)), layer: (row, key) => ftLayerValue(row, key), craftingProgressId: CRAFTING_PROGRESS_STATUS_ID,
    uuid: () => crypto.randomUUID().toUpperCase(), zone: true });
  if (!res.supported) {
    if (input.background) return 'refused';
    if (res.refused) { if (typeof displayWarning === 'function') displayWarning(res.message); return 'refused'; }
    window.dispatchEvent(new CustomEvent('ft:status-slice-unavailable', { detail: { kind: 'status_add', reason: res.reason } }));
    return statusWords(res.reason === 'status-already-held' ? 'The status was not added: that creature already has it.'
      : res.reason === 'trap' ? 'The status was not added: a trap ability is mounted on an object, not added to a creature.'
      : res.reason === 'definition-missing' ? 'The status was not added: this page could not load its definition. Reload the page and try again.'
      : 'The status was not added: this case is not available here.');
  }
  return commitGmBoundary(h.encounterId, h.actorId, { kind: 'add', targetId: h.actorId, itemId: itemKey,
    originId: input.originId,
    casterId: upper(input.casterId ?? '') || h.actorId, durationInt: String(input.durationInt ?? ''), durationType: input.durationType,
    rollPrivate: [0, 1, 2].includes(Number(input.rollPrivate)) ? Number(input.rollPrivate) : 0 }, !input.background, input.stillCurrent, input.onStale);
}

/**
 * S5 / S6 — DELETE a status row: `single` (delete_creature_status_effect_inc.php — the list row's delete), `all`
 * (…_all_connections_inc.php — the status pill) or `all_gm` (delete_status_effect_all_connections_inc.php — the status EDIT form). All
 * The browser decides creature removals and loss hooks; item-held authoring is a raw save.
 * Anything the page cannot locate is refused with words. `legacy` is ignored.
 */
export async function tryCommitStatusDelete(input: { statusId: string; scope: 'single' | 'all' | 'all_gm'; legacy?: () => unknown; logCasterId?: string; c1Retried?: boolean; background?: boolean }): Promise<StatusCommitOutcome> {
  const holder = input.background ? await statusHolder(input.statusId) ?? UNLOADABLE : await locateStatusHolder(input.statusId);
  // Background maintenance reports one named GM skip through its caller, with no tool warning.
  if (input.background && !isCreatureHolder(holder)) return 'refused';
  if (isInventoryHolder(holder)) {
    // N6e: a status on an ITEM. The crafting tracker is the item's own (legacy words); the rest is the legacy remover in the commit.
    if (holder.statusItemId === CRAFTING_PROGRESS_STATUS_ID) return statusWords('This crafting progress marker is managed by the item it tracks and cannot be deleted directly.');
    return (await commitInventoryStatus(holder, 'status_delete', 'removed', { status_id: upper(input.statusId), scope: input.scope })).outcome;
  }
  if (!isCreatureHolder(holder)) return refuseUnlocated(holder, 'removed'); // N6d / N6e: words, never the legacy endpoint
  const h0 = await held(holder.actorId, holder.encounterId);
  if (!h0) return input.background ? 'refused' : refuseUnloadable('removed');
  const res0 = resolveStatusDelete(h0.row as Row, { statusId: input.statusId, craftingProgressId: CRAFTING_PROGRESS_STATUS_ID, definition: id => definitionFor(h0.row as Row, id) });
  if (!res0.supported) {
    if (input.background) return 'refused';
    if (res0.refused) { if (typeof displayWarning === 'function') displayWarning(res0.message); return 'refused'; }
    window.dispatchEvent(new CustomEvent('ft:status-slice-unavailable', { detail: { kind: 'status_delete', reason: res0.reason } }));
    return refuseUnloadable('removed');
  }
  return commitGmBoundary(h0.encounterId, h0.actorId, { kind: 'delete', targetId: h0.actorId, statusId: upper(input.statusId), scope: input.scope, logCasterId: input.logCasterId }, !input.background);
}

/** GM life tools resolve effects in the page, then send one lazy save. */
export async function tryCommitCreatureLife(input: { actorId: string; action: 'kill' | 'resurrect' | 'destroy' | 'flee'; killerId?: string; encounterId?: string }): Promise<StatusCommitOutcome> {
  const h = await held(input.actorId, input.encounterId);
  if (!h) return refuseUnloadable(input.action);
  const res = resolveCreatureLife(h.row as Row, { action: input.action });
  if (!res.supported) return statusWords(res.refused ? res.message : res.reason);
  return commitGmBoundary(h.encounterId, h.actorId, { kind: 'life', targetId: h.actorId, action: input.action, killerId: input.killerId });
}

/**
 * Quantity stacks use the browser boundary; unported stage machines are GM skip rows.
 * An unheld holder is located through the status-row read and the shared loader.
 */
export async function tryCommitStatusStack(input: { statusId: string; direction: 'up' | 'down' }): Promise<StatusCommitOutcome> {
  const holder = await locateStatusHolder(input.statusId);
  if (isInventoryHolder(holder)) return refuseOnItem('re-stacked'); // N6e: legacy parity — the stack endpoint refused an item at its creature gate
  if (!isCreatureHolder(holder)) return refuseUnlocated(holder, 'changed'); // N6d / N6e: words, never the legacy endpoint
  const h = await held(holder.actorId, holder.encounterId);
  if (!h) return refuseUnloadable('changed');
  const entry = statusEntry(h.row as Row, input.statusId);
  const item = upper(entry?.status_item_id ?? asRow(asRow(entry?.status_arr)?.items)?.item_id);
  if (!entry) return refuseUnloadable('changed');
  if (!/^[0-9A-F-]{36}$/.test(item) || (input.direction !== 'up' && input.direction !== 'down')) return statusWords('The status was not changed: this page cannot read which status it is. Reload the page and try again.');
  return commitGmBoundary(h.encounterId, h.actorId, { kind: 'stack', targetId: h.actorId, statusId: upper(input.statusId), direction: input.direction });
}

/** WIP membership needs the worker settlement and completion verdict in the same browser save. */
export function tryCommitCraftingWip(input: { action: 'work' | 'stop'; workerId: string; inventoryId?: string; statusId?: string; encounterId?: string }): Promise<StatusTruthAnswer> {
  return changeCraftMembership(input);
}

// Shared actor-row loading for trigger and GM life controls.
function statusWords(message: string): 'refused' {
  if (typeof displayWarning === 'function') displayWarning(message);
  window.dispatchEvent(new CustomEvent('ft:status-truth-refused-words', { detail: { message } }));
  return 'refused';
}
/** One route for every tool: the encounter outbox for a row in an encounter; an encounter-less row is refused — `rowOnly` is the words
 *  shown, or (the craft island, which shows its own message) a quiet refusal callback. Never posted. */
async function routeStatus(actorId: string, encounterId: string | undefined, noun: string,
  held: (encounterId: string, actorId: string) => StatusCommitOutcome | Promise<StatusCommitOutcome>, rowOnly: string | (() => 'refused')): Promise<StatusCommitOutcome> {
  // Admission in CLICK order, per actor, from before the first await (Codex N6 review P2).
  return runRowOnly(actorId, async () => {
    const where = await ensureActorRowHeld(actorId, encounterId, { allowRowOnly: true, what: noun });
    if (where.kind === 'refused') return statusWords(where.message);
    if (where.kind === 'row') return typeof rowOnly === 'string' ? statusWords(rowOnly) : rowOnly();
    return await held(where.encounterId, where.actorId) ?? statusWords(`${noun} was not changed: this case is not available here.`);
  });
}
const OUTSIDE = 'Bring it into an encounter first.';
/** The trigger badge (R-064): toggle one trigger. */
export function commitTriggerToggle(input: { abilityId: string; actorId: string; encounterId?: string; inactive: unknown }): Promise<StatusCommitOutcome> {
  return routeStatus(input.actorId, input.encounterId, 'The trigger',
    (encounterId, actorId) => tryCommitTriggerToggle({ ...input, actorId, encounterId }),
    `The trigger was not changed: triggers work only inside an encounter. ${OUTSIDE}`);
}
/** Clear Triggers (R-064). */
export function commitTriggerClear(input: { actorId: string; encounterId?: string }): Promise<StatusCommitOutcome> {
  return routeStatus(input.actorId, input.encounterId, 'The triggers',
    (encounterId, actorId) => tryCommitTriggerClear({ actorId, encounterId }),
    `The triggers were not cleared: triggers work only inside an encounter. ${OUTSIDE}`);
}
/** GM kill / resurrect / destroy (R-066). The death pipeline runs once server-side from its stored work record. */
export function commitCreatureLife(input: { actorId: string; action: 'kill' | 'resurrect' | 'destroy' | 'flee'; killerId?: string; encounterId?: string }): Promise<StatusCommitOutcome> {
  const noun = input.action === 'kill' ? 'The kill' : input.action === 'resurrect' ? 'The resurrection' : input.action === 'flee' ? 'The retreat' : 'The destroy';
  const verb = input.action === 'kill' ? 'killed' : input.action === 'resurrect' ? 'resurrected' : input.action === 'flee' ? 'made to flee' : 'destroyed';
  return routeStatus(input.actorId, input.encounterId, noun,
    (encounterId, actorId) => tryCommitCreatureLife({ ...input, actorId, encounterId }),
    `${noun} was not applied: a creature outside an encounter cannot be ${verb}. ${OUTSIDE}`);
}
/** Work/Stop share the activity lane's actor serialization, snapshot and atomic save. */
export async function commitCraftingWip(input: { action: 'work' | 'stop'; workerId: string; inventoryId?: string; statusId?: string; encounterId?: string }): Promise<StatusTruthAnswer> {
  return tryCommitCraftingWip(input);
}

/** The gate verdict the page's browser gate (ability-preconditions-browser.ts) answers for this start, judged once the row is held. */
export type CraftStartGate = (encounterId: string) => Promise<{ kind: 'refuse'; message: string } | { kind: 'allow' } | { kind: 'unknown'; site: string }>;
export type CraftStartOutcome = { outcome: 'handled' | 'refused' };
/** Starting a WIP requires an atomic browser verdict for materials, shell, tracker and first worker. */
export async function commitCraftStart(input: { actorId: string; encounterId?: string; recipe: Row; sizeId?: string | null; rollPrivate?: number;
  gate: CraftStartGate }): Promise<CraftStartOutcome> {
  return startCraft(input);
}

type StatusTruthWireFrame = ActorTruthCommandBase & { kind: StatusTruthKind; rev: number; frame: StatusTruthFrame; gm_boundary?: unknown; applied_frame?: import('../janus-engine/transport/applied-frame').AppliedFrameResult; provisional?: boolean; committed?: boolean; correction?: boolean };
/** Publish-first subscriber: provisional frames render but never bank; committed / correction frames install the saved
 * sections through the per-section revision gate. */
export function handleStatusTruth(command: StatusTruthWireFrame): void {
  if (command?.schema !== STATUS_TRUTH_SCHEMA || !Number.isInteger(command.rev) || command.rev < 0) return;
  if (command.gm_boundary && command.committed) {
    if (!noteFrameSeen(command.operation_id, 'committed')) return;
    const row = actorRowFor(command.encounter_id, command.actor_id);
    if (row) bankConfirmedRev(command.encounter_id, command.actor_id, row, command.rev);
    // The router has already installed these rows and computed sections on every held facet.
    if (command.applied_frame && !('cache_ref' in command.applied_frame)) {
      window.dispatchEvent(new CustomEvent('ft:status-truth-frame', { detail: { kind: 'committed', command } }));
      return;
    }
    const changes = asRow(asRow(command.gm_boundary)?.ec);
    const removed = new Set(Object.entries(changes ?? {}).filter(([, value]) => asRow(value)?.encounter_creature_deleted === 1).map(([id]) => upper(id)));
    // Native departures persist their deletion through the typed row plan, outside live EC scalars.
    for (const write of normalizePhpListish(asRow(asRow(command.gm_boundary)?.spawnRows)?.writes)) {
      if (asRow(write)?.table !== 'encounter_creatures') continue;
      const row = asRow(asRow(write)?.row);
      if (Number(itemField(row, 'encounter_creature_deleted') ?? 0) === 1)
        removed.add(upper(itemField(row, 'encounter_creature_id')));
    }
    retireEncounterCreatures(command.encounter_id, removed);
    void settleGmBoundary(command.encounter_id, { actorId: command.actor_id, rev: command.rev, relay: true });
    window.dispatchEvent(new CustomEvent('ft:status-truth-frame', { detail: { kind: 'committed', command } }));
    return;
  }
  if (command.kind === 'status_hide' || command.kind === 'status_reveal') {
    // S2: no row section — flip any open Hide / Reveal pair for that status.
    const f = asRow(command.frame);
    if (command.committed && f && typeof f.status_id === 'string') showHiddenPair(upper(f.status_id), Number(f.hidden) === 1);
    return;
  }
  const kind = command.correction ? 'correction' : command.committed ? 'committed' : 'provisional';
  const row = actorRowFor(command.encounter_id, command.actor_id);
  const frame = asRow(command.frame) as StatusTruthFrame | null;
  if (!row || !frame) return;
  if (!noteFrameSeen(command.operation_id, kind)) return;
  if (kind === 'provisional') {
    if (!admitProvisional(command.encounter_id, command.actor_id, row, command.rev)) return;
    // A peer's speculative frame never overwrites a section one of OUR pending operations holds (Codex S1 review F7): the
    // next local click would resolve its `before` from the peer's list and be refused for nothing.
    const ours = newerPendingSections(command, command.rev - 1);
    for (const [section, value] of frameSections(frame)) if (!ours.has(section)) applySection(row as Row, section, value);
    render(command, row);
    return;
  }
  bankConfirmedRev(command.encounter_id, command.actor_id, row, command.rev);
  pending.delete(command.operation_id);
  installSaved(command, row as Row, command.rev, frame);
  settleShared(command, row);
  render(command, row);
  window.dispatchEvent(new CustomEvent('ft:status-truth-frame', { detail: { kind, command } }));
}

Object.assign(window, { ftCommitTriggerToggle: commitTriggerToggle, ftCommitTriggerClear: commitTriggerClear, ftCommitCreatureLife: commitCreatureLife,
  ftCommitCraftingWip: commitCraftingWip, ftCommitCraftStart: commitCraftStart, ftCommitGmSkip: commitGmSkip });
Object.assign(window, { ftTryCommitTriggerToggle: tryCommitTriggerToggle, ftTryCommitTriggerClear: tryCommitTriggerClear, ftTryCommitStatusHidden: tryCommitStatusHidden,
  ftTryCommitStatusPause: tryCommitStatusPause, ftTryCommitStatusAdd: tryCommitStatusAdd, ftTryCommitStatusDelete: tryCommitStatusDelete,
  ftTryCommitCreatureLife: tryCommitCreatureLife, ftTryCommitStatusStack: tryCommitStatusStack, ftTryCommitCraftingWip: tryCommitCraftingWip });
